import React from 'react';
import { Button } from '../ui/button';
import { Popover, PopoverTrigger, PopoverContent } from '../ui/popover';
import { useTheme } from '../../context/ThemeContext';
import { cn } from '../../lib/utils';
import api from '../../lib/api';
import { toast } from 'sonner';
import { AlertTriangle, ArrowRight, Rocket, Compass, Target, Flag, FileText, ListPlus } from 'lucide-react';

/**
 * Shared "Diagnose → Prioritise → Plan → Act" spine used across the health
 * dashboards (LEI reference implementation; ROE/PE/ROI/Maturity to follow).
 *
 * A normalized FocusTarget:
 *   { engine, entityId, entityLabel, pillarId, score, target, gapPct, overallScore }
 */

// Per-engine scoring targets + default lift used when handing off to the
// solution models. LEI: measures /80. PE: score /100. ROE: variance → 0.
// ROI: no fixed ceiling (uses current as reference). Maturity: levels /5.
export const ENGINE_TARGETS = {
  lei: { target: 80, lift: 15 },
  pe: { target: 100, lift: 10 },
  roe: { target: 0, lift: 5 },
  roi: { target: null, lift: 10 },
  maturity: { target: 100, lift: 10 },
};

/** Build a normalized FocusTarget from a shared engine action-plan payload. */
export function buildFocusFromEntity(engine, entity) {
  if (!entity || entity.entity_label == null) return null;
  const cfg = ENGINE_TARGETS[engine] || { target: 100, lift: 10 };
  const score = typeof entity.score === 'number' ? Math.round(entity.score * 10) / 10 : null;
  const target = cfg.target;
  const gapPct = target != null && score != null ? Math.max(0, Math.round((target - score) * 10) / 10) : null;
  // ROI hands off a scenario, not a severity band. The action-plan
  // payload carries `scenario_id`; prefer it so "Model the lift" pins
  // the right business case in Portfolio Analysis (entity_id is the
  // band label like "low" and must NOT be used as the focus id).
  const resolvedId = entity.scenario_id || entity.entity_id || entity.entity_label;
  return {
    engine,
    entityId: resolvedId,
    entityLabel: entity.entity_label,
    pillarId: resolvedId,
    score,
    target,
    gapPct,
    lift: cfg.lift,
    overallScore: entity.overall_score != null ? Math.round(entity.overall_score * 10) / 10 : null,
    granularity: 'measure',
  };
}

export function buildLeiFocus(data) {
  const measures = (data?.pillars || []).flatMap((p) =>
    (p.measures || []).map((m) => ({
      pillarId: p.id,
      pillarName: p.name,
      label: m.name || m.measure,
      score: Math.round((m.score || 0) * 10) / 10,
    })),
  );
  if (!measures.length) {
    const pillars = data?.pillars || [];
    if (!pillars.length) return null;
    const lp = [...pillars].sort((a, b) => a.score - b.score)[0];
    return { engine: 'lei', entityId: lp.id, entityLabel: lp.name, pillarId: lp.id, score: Math.round(lp.score), target: 80, gapPct: Math.max(0, 80 - Math.round(lp.score)), overallScore: Math.round(data?.overall_score || 0), granularity: 'pillar' };
  }
  const lowest = measures.reduce((a, b) => (b.score < a.score ? b : a));
  return {
    engine: 'lei',
    entityId: lowest.pillarId,
    entityLabel: lowest.label,
    pillarId: lowest.pillarId,
    pillarName: lowest.pillarName,
    score: lowest.score,
    target: 80,
    gapPct: Math.round((80 - lowest.score) * 10) / 10,
    lift: ENGINE_TARGETS.lei.lift,
    overallScore: Math.round(data?.overall_score || 0),
    granularity: 'measure',
  };
}

/** Route-by-intent handoff into the appropriate model, stamping provenance. */
export function takeAction(focus, intent, navigate) {
  if (!focus) return;
  const provenance = {
    source: `${focus.engine}-health`,
    engine: focus.engine,
    entityLabel: focus.entityLabel,
    pillarId: focus.pillarId,
    score: focus.score,
    target: focus.target,
    ts: Date.now(),
  };
  const lift = focus.lift || 15;
  if (intent === 'model') {
    // Measure/pillar lift → Portfolio Analysis (model the fix, auto-pin).
    // Focus is mirrored into query params too, so a refresh on /portfolio
    // keeps the focused measure (router state is lost on reload).
    const q = new URLSearchParams({
      focusReason: `${focus.engine}-weak-link`,
      focusEngine: focus.engine,
      focusMeasure: focus.pillarId || focus.entityId || '',
      focusLabel: focus.entityLabel || '',
      lift: String(lift),
    });
    navigate(`/portfolio?${q.toString()}`, {
      state: {
        focusReason: `${focus.engine}-weak-link`,
        focusEngine: focus.engine,
        focusMeasure: focus.pillarId,
        focusMeasureLabel: focus.entityLabel,
        focusLEIMeasure: focus.pillarId,
        focusLEIMeasureLabel: focus.entityLabel,
        focusLEILiftPoints: lift,
        autoPin: true,
        provenance,
      },
    });
    return;
  }
  if (intent === 'business-case') {
    // One-step prefilled Business Case Builder. We pass a minimal-but-
    // valid solutionData (generate-sections defaults every field) so the
    // builder opens with a titled draft instead of crashing on missing
    // KPI data.
    navigate('/business-case-builder', {
      state: { solutionData: focusToSolutionData(focus), currency: 'USD', provenance },
    });
    return;
  }
  // "Which interventions close this gap" → Solution Finder
  const q = new URLSearchParams({
    focus: focus.entityLabel,
    label: focus.entityLabel,
    engine: focus.engine,
  });
  if (focus.gapPct != null) q.set('gap', String(focus.gapPct));
  navigate(`/solution-finder?${q.toString()}`, { state: { provenance } });
}

/** Map a FocusTarget → a minimal solutionData payload for the Business
 * Case Builder. Every field the builder reads is defaulted server-side,
 * so this is safe to auto-generate from even when only the weak entity
 * is known. */
export function focusToSolutionData(focus) {
  if (!focus) return null;
  const label = focus.entityLabel || 'learning priority';
  const eng = String(focus.engine).toUpperCase();
  return {
    _prefill_from_focus: true,
    kpi: {
      id: 'productivity',
      name: label,
      issue_statement: `Close the gap on ${label} (current ${focus.score ?? '—'}${focus.target != null ? ` vs target ${focus.target}` : ''}), identified from ${eng} Health.`,
    },
    interventions: [],
    lei_measures: [],
    financial_summary: {},
    scenario_config: {},
    provenance: {
      source: `${focus.engine}-health`,
      engine: focus.engine,
      entityLabel: focus.entityLabel,
      entityId: focus.entityId,
      score: focus.score,
      target: focus.target,
      ts: Date.now(),
    },
  };
}

/** Add the current focus to the Intervention Backlog (people-engine). */
export async function addToBacklog(focus, navigate) {
  if (!focus) return;
  const required = focus.target && focus.target > 0
    ? focus.target
    : (focus.score != null ? Math.ceil(Math.abs(focus.score) * 1.1) : 100);
  const achieved = focus.score != null ? focus.score : 0;
  const eng = String(focus.engine).toUpperCase();
  const payload = {
    skill_name: focus.entityLabel,
    action_plan: `Close the gap on “${focus.entityLabel}” — surfaced from ${eng} Health (current ${focus.score ?? '—'}${focus.target != null ? `, target ${focus.target}` : ''}). Prioritise the top 2-3 interventions tied to this area.`,
    required_level: required,
    achieved_level: achieved,
    notes: `Auto-added from ${eng} Health action spine.`,
    model: 'action-spine',
    ...(focus.organizationId ? { organization_id: focus.organizationId } : {}),
  };
  try {
    await api.post('/people-engine/intervention-backlog', payload);
    toast.success('Added to intervention backlog', {
      description: `“${focus.entityLabel}” is now on your backlog.`,
      action: navigate ? { label: 'View backlog', onClick: () => navigate('/people-engine/intervention-backlog') } : undefined,
    });
  } catch (e) {
    toast.error('Could not add to backlog');
  }
}

/** One-click implement: create a Goal seeded from the FocusTarget + provenance. */
export async function createGoalFromFocus(focus, navigate) {
  if (!focus) return;
  const targetValue = focus.target && focus.target > 0
    ? focus.target
    : (focus.score != null ? Math.ceil(focus.score * 1.1) : 100);
  const payload = {
    goal_type: 'scenario_planning',
    title: `Improve ${focus.entityLabel}`,
    description: `Auto-created from ${String(focus.engine).toUpperCase()} Health — close the gap on “${focus.entityLabel}” (current ${focus.score ?? '—'}${focus.target != null ? `, target ${focus.target}` : ''}).`,
    target_value: targetValue,
    baseline_value: focus.score != null ? focus.score : null,
    timeframe: 'quarterly',
    deadline: '',
    factor_name: 'leadership_alignment',
    lei_measure_id: 'training_scope',
    kpi_id: 'productivity',
    email_notifications: false,
    provenance: {
      source: `${focus.engine}-health`,
      engine: focus.engine,
      entityLabel: focus.entityLabel,
      entityId: focus.entityId,
      score: focus.score,
      target: focus.target,
      ts: Date.now(),
    },
  };
  try {
    await api.post('/goals', payload);
    toast.success('Goal created from this priority', {
      description: `“Improve ${focus.entityLabel}” added to Goals.`,
      action: navigate ? { label: 'View goals', onClick: () => navigate('/goals') } : undefined,
    });
  } catch (e) {
    toast.error('Could not create the goal');
  }
}

/** Dashboard hero tile: shows the lowest-performing measure + link to Priorities. */
export function WeakestMeasureTile({ focus, onView, testId = 'weakest-measure-tile' }) {
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme !== 'light';
  if (!focus) return null;
  return (
    <div
      className={cn(
        'rounded-2xl p-4 flex items-center gap-4 flex-wrap',
        isDark ? 'bg-[#111827] ring-1 ring-[#1f2a44]' : 'bg-white ring-1 ring-rose-100 shadow-sm',
      )}
      data-testid={testId}
    >
      <span className={cn('w-10 h-10 rounded-xl flex items-center justify-center shrink-0', isDark ? 'bg-rose-500/15' : 'bg-rose-50')}>
        <AlertTriangle className="h-5 w-5 text-rose-500" />
      </span>
      <div className="min-w-0 flex-1">
        <div className={cn('text-[10px] font-bold uppercase tracking-[0.14em]', isDark ? 'text-slate-400' : 'text-slate-500')}>
          Lowest-performing {focus.granularityLabel || (focus.granularity === 'measure' ? 'measure' : 'pillar')}
        </div>
        <div className="flex items-baseline gap-2 mt-0.5">
          <span className={cn('text-base font-bold truncate', isDark ? 'text-slate-100' : 'text-slate-800')} title={focus.entityLabel} data-testid={`${testId}-label`}>
            {focus.entityLabel}
          </span>
          <span className="text-sm font-semibold text-rose-500 tabular-nums whitespace-nowrap" data-testid={`${testId}-score`}>
            {focus.scoreLabel != null ? focus.scoreLabel : `${focus.score}${focus.target != null ? ` / ${focus.target}` : ''}`}
          </span>
        </div>
      </div>
      <Button size="sm" variant={isDark ? 'secondary' : 'outline'} onClick={onView} className="shrink-0" data-testid={`${testId}-view`}>
        <Target className="h-4 w-4 mr-1.5" /> View priority <ArrowRight className="h-4 w-4 ml-1" />
      </Button>
    </div>
  );
}

/** Action Plan "Take Action" — route by intent into the right solution model. */
export function TakeActionButton({ focus, navigate, testId = 'take-action' }) {
  if (!focus) return null;
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button size="sm" className="gap-1.5" data-testid={`${testId}-btn`}>
          <Rocket className="h-4 w-4" /> Take Action
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-72 p-2" data-testid={`${testId}-menu`}>
        <p className="px-2 pt-1 pb-2 text-[11px] text-slate-500">
          Act on <span className="font-semibold">{focus.entityLabel}</span>{focus.score != null && ` (${focus.score}${focus.target != null ? ` / ${focus.target}` : ''})`}
        </p>
        <button
          className="w-full text-left rounded-md px-2 py-2 hover:bg-slate-100 dark:hover:bg-white/5 flex items-start gap-2"
          onClick={() => takeAction(focus, 'model', navigate)}
          data-testid={`${testId}-model`}
        >
          <Rocket className="h-4 w-4 mt-0.5 text-indigo-500 shrink-0" />
          <span>
            <span className="block text-sm font-medium">Model the lift</span>
            <span className="block text-[11px] text-slate-500">Portfolio Analysis — model &amp; pin the recommended fix</span>
          </span>
        </button>
        <button
          className="w-full text-left rounded-md px-2 py-2 hover:bg-slate-100 dark:hover:bg-white/5 flex items-start gap-2"
          onClick={() => takeAction(focus, 'interventions', navigate)}
          data-testid={`${testId}-interventions`}
        >
          <Compass className="h-4 w-4 mt-0.5 text-teal-500 shrink-0" />
          <span>
            <span className="block text-sm font-medium">Find interventions</span>
            <span className="block text-[11px] text-slate-500">Solution Finder — pick modalities to close the gap</span>
          </span>
        </button>
        <div className="my-1 border-t border-slate-200/60" />
        <button
          className="w-full text-left rounded-md px-2 py-2 hover:bg-slate-100 dark:hover:bg-white/5 flex items-start gap-2"
          onClick={() => takeAction(focus, 'business-case', navigate)}
          data-testid={`${testId}-business-case`}
        >
          <FileText className="h-4 w-4 mt-0.5 text-sky-500 shrink-0" />
          <span>
            <span className="block text-sm font-medium">Build a business case</span>
            <span className="block text-[11px] text-slate-500">Open a prefilled Business Case Builder for this gap</span>
          </span>
        </button>
        <button
          className="w-full text-left rounded-md px-2 py-2 hover:bg-slate-100 dark:hover:bg-white/5 flex items-start gap-2"
          onClick={() => addToBacklog(focus, navigate)}
          data-testid={`${testId}-backlog`}
        >
          <ListPlus className="h-4 w-4 mt-0.5 text-teal-500 shrink-0" />
          <span>
            <span className="block text-sm font-medium">Add to intervention backlog</span>
            <span className="block text-[11px] text-slate-500">Track this fix on the People Engine backlog</span>
          </span>
        </button>
        <button
          className="w-full text-left rounded-md px-2 py-2 hover:bg-slate-100 dark:hover:bg-white/5 flex items-start gap-2"
          onClick={() => createGoalFromFocus(focus, navigate)}
          data-testid={`${testId}-goal`}
        >
          <Flag className="h-4 w-4 mt-0.5 text-amber-500 shrink-0" />
          <span>
            <span className="block text-sm font-medium">Create a goal</span>
            <span className="block text-[11px] text-slate-500">Track this fix as a goal (added to Goals)</span>
          </span>
        </button>
      </PopoverContent>
    </Popover>
  );
}
